// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ActionMenuItem } from '@/components/action-menu-item';
import { EducationalTooltip } from '@/components/educational-tooltip';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { SyncStatusFilterMenu } from '@/components/sync-status-filter-menu';
import { TableControlContainer } from '@/components/table-control-container';
import {
  CrmSyncStatus,
  crmSyncStatusSchema,
} from '@/constants/crm-sync-status';
import {
  AddRounded,
  ArrowDropDownRounded,
  InfoOutlined,
  MultipleStopOutlined,
  SettingsRounded,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import { CustomerFilterMenu } from '@/customer/components/customer-filter-menu';
import { useDisclosure } from '@/hooks/use-disclosure';
import { LocationFilter } from '@/location/api/get-locations';
import { CreateLocationDialog } from '@/location/components/create-location-dialog';
import { LocationDataImportDialog } from '@/location/components/location-data-import-dialog';
import { SiteTable } from '@/location/components/site-table';
import {
  LocationSortProperty,
  locationSortPropertySchema,
} from '@/location/hooks/use-locations';
import { searchTermSchema } from '@/utilities/search-term';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { debounce, omit } from 'lodash-es';
import { ChangeEventHandler, Suspense, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

const DEFAULT_SORT_PROPERTY: LocationSortProperty = 'name';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Tooltip text explaining the archived filter.
 * Helps users understand what archived locations are.
 */
const SHOW_ARCHIVED_TOOLTIP_TITLE =
  'Show archived location records, which are locations that a user has archived for non-use. Archived locations do not appear by default in the location list.';
const SHOW_TRANSIENT_TOOLTIP_TITLE =
  'Show transient location records, which are used for only occasional or temporary stops. Transient locations do not appear by default in the location list.';

const searchSchema = z.object({
  query: searchTermSchema,
  archived: z.boolean().optional(),
  transient: z.boolean().optional(),
  customers: z.array(z.string()).optional(),
  syncStatuses: z.array(crmSyncStatusSchema).optional(),
  direction: sortDirectionSchema.optional(),
  sort: locationSortPropertySchema.optional(),
  page: z.number().optional(),
  new: z.string().optional(),
});

export const Route = createFileRoute('/_app/locations/')({
  component: LocationsRoute,
  validateSearch: searchSchema,
});

function LocationsRoute() {
  const actionGroupRef = useRef<HTMLDivElement>(null);

  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchValue, setSearchValue] = useState(search.query ?? '');

  const menu = useDisclosure();

  // If the 'new' search parameter is present, automatically open the creation dialog.
  const createDialog = useDisclosure(Boolean(search.new));
  const uploadDialog = useDisclosure();
  const actionMenu = useDisclosure();
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const debouncedUpdate = useMemo(
    () =>
      debounce((value: string) => {
        navigate({
          search: { ...omit(search, 'page'), query: value || undefined },
        });
      }, 300),
    [navigate, search]
  );

  const customerIds = search.customers ?? [];
  const defaultLocationName = search.new
    ? decodeURIComponent(search.new)
    : undefined;

  const sort = {
    property: search.sort ?? DEFAULT_SORT_PROPERTY,
    direction: search.direction ?? DEFAULT_SORT_DIRECTION,
  };

  const isBadgeInvisible =
    search.archived === undefined && search.transient === undefined;

  const handleChangeArchived = (_: unknown, isChecked: boolean) => {
    if (isChecked) {
      return navigate({
        search: { ...omit(search, 'page'), archived: true },
      });
    }

    navigate({ search: { ...omit(search, 'page'), archived: undefined } });
  };

  const handleChangeTransient = (_: unknown, isChecked: boolean) => {
    if (isChecked) {
      return navigate({
        search: { ...omit(search, 'page'), transient: true },
      });
    }

    navigate({ search: { ...omit(search, 'page'), transient: undefined } });
  };

  const setSort = (
    property: LocationSortProperty,
    direction: SortDirection
  ) => {
    navigate({
      search: { ...omit(search, 'page'), direction, sort: property },
    });
  };

  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedUpdate(value);
  };

  const handleChangeCustomers = (ids: string[]) => {
    navigate({
      search: {
        ...omit(search, 'page'),
        customers: ids.length > 0 ? ids : undefined,
      },
    });
  };

  const handleChangeSyncStatuses = (syncStatuses: CrmSyncStatus[]) => {
    navigate({
      search: {
        ...omit(search, 'page'),
        syncStatuses: syncStatuses.length > 0 ? syncStatuses : undefined,
      },
    });
  };

  const handleSort = (property: LocationSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const handlePageChange = (page: number) => {
    navigate({ search: { ...search, page } });
  };

  const handleCloseDialog = () => {
    createDialog.close();
    navigate({ search: omit(search, 'new') });
  };

  const filter: LocationFilter = {
    query: search.query,
    isArchived: search.archived ? 'all' : undefined,
    isTransient: search.transient ? 'all' : undefined,
    customerIds: search.customers,
    syncStatuses: search.syncStatuses,
    page: search.page ?? 1,
    pageSize: 50,
    sort,
  };

  const exportURL = new URL(
    '/v1/export/locations',
    import.meta.env.VITE_API_URL
  );

  return (
    <Stack>
      <CreateLocationDialog
        defaultName={defaultLocationName}
        isOpen={createDialog.isOpen}
        onClose={handleCloseDialog}
      />
      <LocationDataImportDialog
        isOpen={uploadDialog.isOpen}
        onClose={uploadDialog.close}
      />
      <PageContent>
        {/* Header with title and info icon */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">Locations</Typography>
            <IconButton
              ref={infoButtonRef}
              size="small"
              onClick={infoTooltip.open}
              aria-label="Learn about locations"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Stack>

          {/* Educational tooltip explaining location types */}
          <EducationalTooltip
            title="Locations"
            isOpen={infoTooltip.isOpen}
            onClose={infoTooltip.close}
            anchorElement={infoButtonRef.current}
            placement="bottom-start"
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Location owner
                </Typography>
                <Typography variant="body2">
                  <strong>Manufacturer locations</strong> are associated with
                  your organization.{' '}
                  <strong>Customer/supplier locations</strong> are associated
                  with a company you have created (either a supplier or
                  customer).
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Inventory and non-inventory
                </Typography>
                <Typography variant="body2">
                  <strong>Inventory locations</strong> count assets in stock
                  rather than as deployed.{' '}
                  <strong>Non-inventory locations</strong> count assets as
                  deployed.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Examples
                </Typography>
                <Typography
                  variant="body2"
                  component="ul"
                  sx={{ pl: 2, mb: 0, '& li': { mb: 0.5 } }}
                >
                  <li>
                    <strong>Manufacturer inventory.</strong> You might have
                    inventory at your own warehouse or headquarters.
                  </li>
                  <li>
                    <strong>Supplier inventory.</strong> You might have
                    inventory at a third-party supplier (such as a 3PL
                    warehouse).
                  </li>
                  <li>
                    <strong>Customer inventory.</strong> You might have
                    inventory at a customer if you&apos;re stocking deployed
                    spares.
                  </li>
                  <li>
                    <strong>Manufacturer non-inventory.</strong> You might have
                    a non-inventory location such as a repair facility or
                    service center.
                  </li>
                  <li>
                    <strong>Customer non-inventory.</strong> Most of your
                    customers will have a non-inventory location, where you have
                    deployed assets.
                  </li>
                </Typography>
              </Box>
              <Typography variant="body2">
                <Link
                  href="https://guide.hardfin.com/set-up-the-basics/locations"
                  target="_blank"
                >
                  Read more about locations
                </Link>
              </Typography>
            </Stack>
          </EducationalTooltip>

          {/* Action bar */}
          <Stack direction="row" spacing={1}>
            <ButtonGroup
              size="small"
              variant="contained"
              ref={actionGroupRef}
              sx={{ alignSelf: 'end' }}
            >
              <Button startIcon={<AddRounded />} onClick={createDialog.open}>
                Add location
              </Button>
              <Button onClick={actionMenu.open}>
                <ArrowDropDownRounded fontSize="small" />
              </Button>
            </ButtonGroup>
            <Menu
              open={actionMenu.isOpen}
              onClose={actionMenu.close}
              onClick={actionMenu.close}
              anchorEl={actionGroupRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ disablePadding: true }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
                },
              }}
            >
              <ActionMenuItem
                icon={<MultipleStopOutlined color="action" fontSize="small" />}
                label="Import location data"
                description="Import location data in bulk from a CSV file"
                onClick={uploadDialog.open}
              />
            </Menu>
          </Stack>
        </Stack>
        <TableControlContainer>
          <Stack direction="row" marginRight="auto" spacing={1}>
            <CustomerFilterMenu
              includeOwnOrganization
              value={customerIds}
              onChange={handleChangeCustomers}
            />
            <SyncStatusFilterMenu
              value={search.syncStatuses ?? []}
              onChange={handleChangeSyncStatuses}
            />
          </Stack>
          <TextField
            size="small"
            label="Search locations"
            value={searchValue}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={handleChangeQuery}
            sx={{ width: { xs: '100%', sm: '20rem' } }}
          />
          <IconButton ref={menuButtonRef} onClick={menu.open}>
            <Badge variant="dot" color="error" invisible={isBadgeInvisible}>
              <SettingsRounded fontSize="small" />
            </Badge>
          </IconButton>
          <Popover
            anchorEl={menuButtonRef.current}
            open={menu.isOpen}
            onClose={menu.close}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Stack paddingY={0.5}>
              <Tooltip
                arrow
                placement="left"
                title={SHOW_ARCHIVED_TOOLTIP_TITLE}
              >
                <FormControlLabel
                  sx={{ paddingLeft: 2, paddingY: 0.5 }}
                  label={
                    <Typography variant="body2">
                      Include archived locations
                    </Typography>
                  }
                  control={
                    <Checkbox
                      size="small"
                      checked={search.archived ?? false}
                      onChange={handleChangeArchived}
                    />
                  }
                />
              </Tooltip>
              <Tooltip
                arrow
                placement="left"
                title={SHOW_TRANSIENT_TOOLTIP_TITLE}
              >
                <FormControlLabel
                  sx={{ paddingLeft: 2, paddingY: 0.5 }}
                  label={
                    <Typography variant="body2">
                      Include transient locations
                    </Typography>
                  }
                  control={
                    <Checkbox
                      size="small"
                      checked={search.transient ?? false}
                      onChange={handleChangeTransient}
                    />
                  }
                />
              </Tooltip>
            </Stack>
          </Popover>
        </TableControlContainer>
        <SupportErrorBoundary message="An error occurred.">
          <Suspense>
            <SiteTable
              sort={sort}
              filter={filter}
              onSort={handleSort}
              onPageChange={handlePageChange}
              exportURL={exportURL}
            />
          </Suspense>
        </SupportErrorBoundary>
      </PageContent>
    </Stack>
  );
}
